/**
 * Country list for nationality picker.
 * Sorted alphabetically in French. English labels provided for EN UI.
 * Flag emoji derived from ISO 3166-1 alpha-2 regional indicator symbols.
 */

export interface Country {
  code: string; // ISO 3166-1 alpha-2
  fr: string;
  en: string;
}

/** Convert a 2-letter country code to its flag emoji */
export function countryFlag(code: string): string {
  if (!code || code.length !== 2) return "";
  const base = 0x1f1e6 - 65; // 'A' = 65
  return String.fromCodePoint(base + code.charCodeAt(0), base + code.charCodeAt(1));
}

/**
 * Francophone country codes — used to auto-detect locale from IP geolocation.
 * If the user's IP resolves to one of these countries, default locale = "fr".
 */
export const FRANCOPHONE_COUNTRIES = new Set([
  "FR",
  "BE",
  "CH",
  "CA",
  "LU",
  "MC",
  "SN",
  "CI",
  "ML",
  "BF",
  "NE",
  "TD",
  "GN",
  "RW",
  "BI",
  "BJ",
  "TG",
  "CF",
  "CG",
  "CD",
  "CM",
  "GA",
  "DJ",
  "KM",
  "MG",
  "MU",
  "SC",
  "HT",
  "RE",
  "GP",
  "MQ",
  "GF",
  "YT",
  "NC",
  "PF",
  "WF",
  "BL",
  "MF",
  "PM",
]);

export const COUNTRIES: Country[] = [
  { code: "AF", fr: "Afghanistan", en: "Afghanistan" },
  { code: "ZA", fr: "Afrique du Sud", en: "South Africa" },
  { code: "AL", fr: "Albanie", en: "Albania" },
  { code: "DZ", fr: "Algérie", en: "Algeria" },
  { code: "DE", fr: "Allemagne", en: "Germany" },
  { code: "AD", fr: "Andorre", en: "Andorra" },
  { code: "AO", fr: "Angola", en: "Angola" },
  { code: "SA", fr: "Arabie saoudite", en: "Saudi Arabia" },
  { code: "AR", fr: "Argentine", en: "Argentina" },
  { code: "AM", fr: "Arménie", en: "Armenia" },
  { code: "AU", fr: "Australie", en: "Australia" },
  { code: "AT", fr: "Autriche", en: "Austria" },
  { code: "AZ", fr: "Azerbaïdjan", en: "Azerbaijan" },
  { code: "BH", fr: "Bahreïn", en: "Bahrain" },
  { code: "BD", fr: "Bangladesh", en: "Bangladesh" },
  { code: "BE", fr: "Belgique", en: "Belgium" },
  { code: "BJ", fr: "Bénin", en: "Benin" },
  { code: "BY", fr: "Biélorussie", en: "Belarus" },
  { code: "BO", fr: "Bolivie", en: "Bolivia" },
  { code: "BA", fr: "Bosnie-Herzégovine", en: "Bosnia and Herzegovina" },
  { code: "BW", fr: "Botswana", en: "Botswana" },
  { code: "BR", fr: "Brésil", en: "Brazil" },
  { code: "BN", fr: "Brunei", en: "Brunei" },
  { code: "BG", fr: "Bulgarie", en: "Bulgaria" },
  { code: "BF", fr: "Burkina Faso", en: "Burkina Faso" },
  { code: "BI", fr: "Burundi", en: "Burundi" },
  { code: "KH", fr: "Cambodge", en: "Cambodia" },
  { code: "CM", fr: "Cameroun", en: "Cameroon" },
  { code: "CA", fr: "Canada", en: "Canada" },
  { code: "CL", fr: "Chili", en: "Chile" },
  { code: "CN", fr: "Chine", en: "China" },
  { code: "CY", fr: "Chypre", en: "Cyprus" },
  { code: "CO", fr: "Colombie", en: "Colombia" },
  { code: "KR", fr: "Corée du Sud", en: "South Korea" },
  { code: "CR", fr: "Costa Rica", en: "Costa Rica" },
  { code: "CI", fr: "Côte d'Ivoire", en: "Ivory Coast" },
  { code: "HR", fr: "Croatie", en: "Croatia" },
  { code: "CU", fr: "Cuba", en: "Cuba" },
  { code: "DK", fr: "Danemark", en: "Denmark" },
  { code: "DJ", fr: "Djibouti", en: "Djibouti" },
  { code: "EG", fr: "Égypte", en: "Egypt" },
  { code: "AE", fr: "Émirats arabes unis", en: "United Arab Emirates" },
  { code: "EC", fr: "Équateur", en: "Ecuador" },
  { code: "ES", fr: "Espagne", en: "Spain" },
  { code: "EE", fr: "Estonie", en: "Estonia" },
  { code: "US", fr: "États-Unis", en: "United States" },
  { code: "ET", fr: "Éthiopie", en: "Ethiopia" },
  { code: "FI", fr: "Finlande", en: "Finland" },
  { code: "FR", fr: "France", en: "France" },
  { code: "GA", fr: "Gabon", en: "Gabon" },
  { code: "GE", fr: "Géorgie", en: "Georgia" },
  { code: "GH", fr: "Ghana", en: "Ghana" },
  { code: "GR", fr: "Grèce", en: "Greece" },
  { code: "GT", fr: "Guatemala", en: "Guatemala" },
  { code: "GN", fr: "Guinée", en: "Guinea" },
  { code: "HT", fr: "Haïti", en: "Haiti" },
  { code: "HN", fr: "Honduras", en: "Honduras" },
  { code: "HK", fr: "Hong Kong", en: "Hong Kong" },
  { code: "HU", fr: "Hongrie", en: "Hungary" },
  { code: "IN", fr: "Inde", en: "India" },
  { code: "ID", fr: "Indonésie", en: "Indonesia" },
  { code: "IQ", fr: "Irak", en: "Iraq" },
  { code: "IR", fr: "Iran", en: "Iran" },
  { code: "IE", fr: "Irlande", en: "Ireland" },
  { code: "IS", fr: "Islande", en: "Iceland" },
  { code: "IL", fr: "Israël", en: "Israel" },
  { code: "IT", fr: "Italie", en: "Italy" },
  { code: "JM", fr: "Jamaïque", en: "Jamaica" },
  { code: "JP", fr: "Japon", en: "Japan" },
  { code: "JO", fr: "Jordanie", en: "Jordan" },
  { code: "KZ", fr: "Kazakhstan", en: "Kazakhstan" },
  { code: "KE", fr: "Kenya", en: "Kenya" },
  { code: "KW", fr: "Koweït", en: "Kuwait" },
  { code: "LV", fr: "Lettonie", en: "Latvia" },
  { code: "LB", fr: "Liban", en: "Lebanon" },
  { code: "LT", fr: "Lituanie", en: "Lithuania" },
  { code: "LU", fr: "Luxembourg", en: "Luxembourg" },
  { code: "MK", fr: "Macédoine du Nord", en: "North Macedonia" },
  { code: "MG", fr: "Madagascar", en: "Madagascar" },
  { code: "MY", fr: "Malaisie", en: "Malaysia" },
  { code: "ML", fr: "Mali", en: "Mali" },
  { code: "MT", fr: "Malte", en: "Malta" },
  { code: "MA", fr: "Maroc", en: "Morocco" },
  { code: "MU", fr: "Maurice", en: "Mauritius" },
  { code: "MX", fr: "Mexique", en: "Mexico" },
  { code: "MD", fr: "Moldavie", en: "Moldova" },
  { code: "MC", fr: "Monaco", en: "Monaco" },
  { code: "MN", fr: "Mongolie", en: "Mongolia" },
  { code: "ME", fr: "Monténégro", en: "Montenegro" },
  { code: "MZ", fr: "Mozambique", en: "Mozambique" },
  { code: "NA", fr: "Namibie", en: "Namibia" },
  { code: "NP", fr: "Népal", en: "Nepal" },
  { code: "NI", fr: "Nicaragua", en: "Nicaragua" },
  { code: "NE", fr: "Niger", en: "Niger" },
  { code: "NG", fr: "Nigeria", en: "Nigeria" },
  { code: "NO", fr: "Norvège", en: "Norway" },
  { code: "NZ", fr: "Nouvelle-Zélande", en: "New Zealand" },
  { code: "OM", fr: "Oman", en: "Oman" },
  { code: "UG", fr: "Ouganda", en: "Uganda" },
  { code: "UZ", fr: "Ouzbékistan", en: "Uzbekistan" },
  { code: "PK", fr: "Pakistan", en: "Pakistan" },
  { code: "PA", fr: "Panama", en: "Panama" },
  { code: "PY", fr: "Paraguay", en: "Paraguay" },
  { code: "NL", fr: "Pays-Bas", en: "Netherlands" },
  { code: "PE", fr: "Pérou", en: "Peru" },
  { code: "PH", fr: "Philippines", en: "Philippines" },
  { code: "PL", fr: "Pologne", en: "Poland" },
  { code: "PT", fr: "Portugal", en: "Portugal" },
  { code: "QA", fr: "Qatar", en: "Qatar" },
  { code: "CD", fr: "Rép. dém. du Congo", en: "DR Congo" },
  { code: "DO", fr: "République dominicaine", en: "Dominican Republic" },
  { code: "CZ", fr: "République tchèque", en: "Czech Republic" },
  { code: "RO", fr: "Roumanie", en: "Romania" },
  { code: "GB", fr: "Royaume-Uni", en: "United Kingdom" },
  { code: "RU", fr: "Russie", en: "Russia" },
  { code: "RW", fr: "Rwanda", en: "Rwanda" },
  { code: "SN", fr: "Sénégal", en: "Senegal" },
  { code: "RS", fr: "Serbie", en: "Serbia" },
  { code: "SG", fr: "Singapour", en: "Singapore" },
  { code: "SK", fr: "Slovaquie", en: "Slovakia" },
  { code: "SI", fr: "Slovénie", en: "Slovenia" },
  { code: "SE", fr: "Suède", en: "Sweden" },
  { code: "CH", fr: "Suisse", en: "Switzerland" },
  { code: "TW", fr: "Taïwan", en: "Taiwan" },
  { code: "TZ", fr: "Tanzanie", en: "Tanzania" },
  { code: "TD", fr: "Tchad", en: "Chad" },
  { code: "TH", fr: "Thaïlande", en: "Thailand" },
  { code: "TG", fr: "Togo", en: "Togo" },
  { code: "TN", fr: "Tunisie", en: "Tunisia" },
  { code: "TR", fr: "Turquie", en: "Turkey" },
  { code: "UA", fr: "Ukraine", en: "Ukraine" },
  { code: "UY", fr: "Uruguay", en: "Uruguay" },
  { code: "VE", fr: "Venezuela", en: "Venezuela" },
  { code: "VN", fr: "Vietnam", en: "Vietnam" },
  { code: "ZM", fr: "Zambie", en: "Zambia" },
  { code: "ZW", fr: "Zimbabwe", en: "Zimbabwe" },
];

/** Get country label in the current locale */
export function countryLabel(code: string, locale: "fr" | "en"): string {
  const country = COUNTRIES.find((c) => c.code === code);
  if (!country) return code;
  return country[locale];
}
